import { KWELLO_ICON } from 'common/components/icon/KwelloSvgIcon';
import { PowerBiPageName, PowerBiReportId, PowerBiWorkspaceId } from './power-bi.model';

export interface PageConfigModel {
  appConfig: AppConfig;
  lastActiveModule: number | null;
  lastActiveProject: { [key: number]: string };
}

export enum Claim {
  Alignment = 'Alignment',
  Insights = 'Insights',
  Interaction = 'Interaction',
  Hidden = 'Hidden',
}

export interface AppConfig {
  kwelloUserId: string;
  /** Shared cross-platform person id (#194); null until the API's identity key is set. */
  personId?: string | null;
  /** Server-signed Intercom identity JWT (#23); null when the API has no Intercom secret. */
  intercomUserJwt?: string | null;
  fullName: string;
  firstName: string;
  logo: string;
  modules: AppModule[];
}

export interface AppModule {
  moduleID: number;
  moduleName: string;
  moduleSortOrder: number;
  moduleIconName: string;
  projects: AppProject[];
  route: string;
  moduleTag?: string;
  routeIsExternalLink?: boolean;
  customChildren?: JSX.Element;
}

export interface AppProject {
  moduleProjectID: string;
  projectClientName: string;
  projectDisplayName: string;
  subClientName: string;
  secretName: string;
  ocpApimSubscriptionKey: string;
  openAiTemperature: number;
  parameters: ProjectParameters[];
  filters: CustomFilter[];
  teams: AppTeam[];
  pages: AppPage[];
  claims: Claim[];
  powerBIWorkspaceID: PowerBiWorkspaceId;
}

export interface AppPage {
  Claims: Claim[];
  Filters: CustomFilter[];
  PageDisplayName: string;
  PageID: number;
  PageIconName: KWELLO_ICON;
  PageSortOrder: number;
  ParentPageID?: number;
  Route: string;
  isMenuBar: boolean;
  isTabBar: boolean;
  Tag?: string;
  PowerBIPageName?: PowerBiPageName;
  PowerBIReportID?: PowerBiReportId;
}

export interface MenuItem {
  projectId?: string;
  sortOrder: number;
  id: string;
  tag?: string;
  text: string;
  icon?: KWELLO_ICON;
  customIcon?: string;
  children?: MenuItem[];
  customChildren?: JSX.Element;
  type?: 'subHeading';
  route?: string;
  external?: boolean;
  isMenuBar?: boolean;
  isTabBar?: boolean;
  powerBIPageName?: PowerBiPageName;
  powerBIReportID?: PowerBiReportId;
}

export interface AppTeam {
  teamID: string;
  teamDisplayName: string;
}

export enum Feature {
  AddToDashboard = 'AddToDashboard',
  AiBanner = 'AiBanner',
  AlignmentPreviousLatestFilterSankey = 'AlignmentPreviousLatestFilterSankey', // Temporary feature flag for hiding filters
  BarChartOptions = 'BarChartOptions',
  BryntumGrids = 'BryntumGrids',
  CaptureFileUpload = 'CaptureFileUpload',
  CaptureInsightSource = 'CaptureInsightSource',
  CaptureInsightSourceRequired = 'CaptureInsightSourceRequired',
  CaptureOrganicFileUpload = 'CaptureOrganicFileUpload',
  CaptureOrganicInsights = 'CaptureOrganicInsights',
  CaptureShowInteractions = 'CaptureShowInteractions',
  CaptureWritingAssistantRequired = 'CaptureWritingAssistantRequired',
  CaptureWritingAssistantReview = 'CaptureWritingAssistantReview',
  CoachTeamDetailTabs = 'CoachTeamDetailTabs',
  DataGridGrouping = 'DataGridGrouping',
  DataGridInlineSearch = 'DataGridInlineSearch',
  Discussions = 'Discussion',
  ElsieAiSummaryButton = 'ElsieAiSummaryButton',
  FilterProfileCountry = 'FilterProfileCountry',
  FractionBarCells = 'FractionBarCells',
  InteractionDetailsGrid = 'InteractionDetailsGrid',
  LineChartOptions = 'LineChartOptions',
  MultipleQuestionExport = 'MultipleQuestionExport',
  MultipleQuestionExportToggleQuestionType = 'MultipleQuestionExportToggleQuestionType',
  ProfileInteractionDetailsGrid = 'ProfileInteractionDetailsGrid',
  SavePageAsCustomDashboard = 'SavePageAsDashboard',
  Sentiments = 'Sentiments',
  ThemeAnalyticsPageSearchBar = 'ThemeAnalyticsPageSearchBar',
  pptExportSummary = 'pptExportSummary',
  pptExportSummaryCoach = 'pptExportSummaryCoach', // Temporary feature flag for reports
  pptExportSummaryReports = 'pptExportSummaryReports', // Temporary feature flag for reports
}

export enum ProjectParameter {
  IncludeAlignment = 'includeAlignment',
  Feature = 'feature',
  ShowEmbeddedBookmarks = 'ShowEmbeddedBookmarks',
  ShowEmbeddedFilters = 'ShowEmbeddedFilters',
  ShowEmbeddedPages = 'ShowEmbeddedPages',
  ShowEmbeddedBackground = 'ShowEmbeddedBackground',
}

export interface ProjectParameters {
  name: ProjectParameter;
  value: string;
}

export type FilterAttributeType =
  | 'AccountAttribute'
  | 'AccountHierarchyAttribute'
  | 'InteractionAttribute'
  | 'InteractionDiscussionAttribute'
  | 'MedicalInquiryAttribute'
  | 'ProductAttribute'
  | 'ProfileAttribute'
  | 'SurveyAttribute'
  | 'SurveyQuestionResponseAttribute'
  | 'SurveyTargetAttribute'
  | 'TeamAttribute'
  | 'UserAttribute';

export interface CustomFilter {
  attributeType: FilterAttributeType;
  attributes: CustomFilterAttribute[];
}

export interface CustomFilterAttribute {
  attributeName: string;
  attributeDisplayAlias: string;
}
